import { Chart as ChartJS, registerables } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { LocalizeContext } from 'react-locale-language';
import { getTranslatedMonth } from '../../../../../helpers/localization';
import ActionSummaryView from './ActionSummaryView';
import BalanceSheet from './BalanceSheet';
import StrategyActions from './StrategyActions';
import StrategyChart from './charts/StrategyChart';
import { findLinkedLiabilities } from './helper';
import RiskManagement from './risk-management/RiskManagement';
import StressTest from './stress-test/StressTest';
import RichTextEditor from '../../../../common/rich-text-editor/RichTextEditor';
import { Button as ButtonTW } from '../../../../ui/button';
import OneOffEventSheet from './OneOffEventSheet';
import RepeatingChanges from './RepeatingChanges';

ChartJS.register(...registerables, zoomPlugin);

const getTabs = (translate, stressTestApplied) => [
  { label: translate('balance_sheet'), value: 1 },
  { label: translate('cashflow'), value: 2 },
  { label: translate('repeating_changes'), value: 3 },
  { label: translate('one_time_events'), value: 4 },
  { label: translate('risk_management'), value: 5 },
  { label: translate('graphs'), value: 6 },
  { label: translate('summary_of_actions'), value: 7 },
  ...(stressTestApplied ? [{ label: translate('stress_test'), value: 8 }] : [])
];

const calculateTaxSummary = (strategy, translate) => {
  return strategy.assets
    .filter(asset => asset.category === 'Real Estate')
    .map(asset => {
      const liabilities = findLinkedLiabilities(strategy.liabilities, asset);
      const totalLoanValue = liabilities.filter(l => !l.isDebtLoan).reduce((acc, l) => acc + l.value, 0);

      return {
        asset: asset.name,
        buyingMonth: getTranslatedMonth(translate, asset.buyingMonth, { short: true }),
        buyingYear: asset.buyingYear,
        buyValue: asset.value,
        buyTaxAmount: asset.editMode ? asset?.tax?.buy || 0 : 0,
        additionalBuyCosts: asset.editMode ? asset.purchaseCost || 0 : 0,
        sellingMonth: asset.sellingYear ? getTranslatedMonth(translate, asset.sellingMonth, { short: true }) : '',
        sellingYear: asset.sellingYear,
        soldValue: asset.soldValueWithoutOtherCosts,
        sellTaxAmount: asset.tax?.sell,
        additionalSalesCosts: asset.salesCost,
        totalEquityForBuying: asset.value - totalLoanValue
      };
    })
    ?.sort((a, b) => a.buyingYear - b.buyingYear);
};

const Strategy = ({
  strategy,
  calculatedStrategy,
  armageddon,
  stressTestApplied,
  disabled,
  quickRenaming,
  editable,
  assetLiabilityEditable,
  cashflowEditable,
  reinvestmentsEditable,
  onAssetResort,
  onAddAssetClick,
  onAddLiabilityClick,
  onDuplicateAssetLiabilityClick,
  onAssetLiabilityEditClick,
  onAssetLiabilityDeleteClick,
  onAddEventClick,
  onEditEventClick,
  onOneOffEventDeleteClick,
  onItemQuickRenameSubmit,
  onAddCashflowClick,
  onEditCashflowClick,
  onDeleteCashflowClick,
  onAddReinvestmentOption,
  onEditReinvestmentOption,
  onDeleteReinvestmentOption,
  onDisableReinvestmentOption,
  onDisableAllReinvestmentsOptions,
  editingNotes,
  onNotesSave,
  onNotesAreaClick,
  adminCompTable,
  customerCompTable,
  readOnlyMode,
  onInflationApplyCheckedChange,
  inflationValue,
  onInflationValueChange,
  inflationApplied,
  setStressTestApplied,
  showStressTestAndArmaggedonToClient,
  setBalanceSheetCashflowCsvMeta
}) => {
  const { translate, langCode } = useContext(LocalizeContext);
  const [activeTab, setActiveTab] = useState(1);

  const tabs = useMemo(() => getTabs(translate, stressTestApplied), [langCode, stressTestApplied]);

  const selectTab = value => {
    setActiveTab(() => value);
  };

  useEffect(() => {
    if (!stressTestApplied && activeTab === 5) {
      setActiveTab(1);
    }
  }, [stressTestApplied]);

  const taxSummary = useMemo(
    () => calculatedStrategy && calculateTaxSummary(calculatedStrategy, translate),
    [calculatedStrategy, langCode]
  );
  const actionSummaryStrategy = useMemo(
    () => (calculatedStrategy ? { ...calculatedStrategy, oneOffChanges: strategy?.oneOffChanges || [] } : null),
    [calculatedStrategy, strategy?.oneOffChanges]
  );

  return (
    <div className={`py-2 px-4 fade-in`}>
      <div className="">
        <h6 className="mb-2 large">{translate('strategy_summary')}</h6>
        <Alert className="p-0 mb-0 large bg-transparent border" onClick={editable ? onNotesAreaClick : undefined}>
          <div className="text-dark" style={{ whiteSpace: 'pre-line' }}>
            <RichTextEditor
              richText={editingNotes}
              disabled={readOnlyMode}
              hideToolbar={true}
              autoSave={true}
              className="bg-transparent p-0"
              onSubmit={onNotesSave}
              focusOnMount={false}
              minHeight={150}
              placeholder={translate('nothing_to_show')}
              compKey={strategy?._id}
            />
          </div>
        </Alert>
      </div>

      <div className="py-4 align-items-center gap-4" style={{ whiteSpace: 'nowrap' }}>
        <div className="overflow-auto">
          {tabs.map(tab => (
            <ButtonTW
              className={`bg-secondary text-secondary-dark shadow-none hover:bg-[rgb(var(--bs-secondary-dark-rgb),0.8)] hover:text-white ${
                activeTab == tab.value ? 'bg-secondary-dark text-white' : ''
              }`}
              key={tab.label}
              onClick={() => selectTab(tab.value)}
            >
              <span>{tab.label}</span>
            </ButtonTW>
          ))}
        </div>
        {!readOnlyMode && (
          <div className="mt-4 overflow-auto">
            <StrategyActions
              assetLiabilityEditable={assetLiabilityEditable}
              cashflowEditable={cashflowEditable}
              reinvestmentsEditable={reinvestmentsEditable}
              onAddAssetClick={onAddAssetClick}
              onAddLiabilityClick={onAddLiabilityClick}
              onAddCashflowClick={onAddCashflowClick}
              onAddReinvestmentOption={onAddReinvestmentOption}
              disabled={disabled}
              translate={translate}
              onInflationApplyCheckedChange={onInflationApplyCheckedChange}
              inflationValue={inflationValue}
              onInflationValueChange={onInflationValueChange}
              inflationApplied={inflationApplied}
              stressTestApplied={stressTestApplied}
              setStressTestApplied={setStressTestApplied}
              showStressTestAndArmaggedonToClient={showStressTestAndArmaggedonToClient}
              readOnlyMode={readOnlyMode}
            />
          </div>
        )}
      </div>

      {calculatedStrategy && (
        <div>
          {activeTab == 1 && (
            <BalanceSheet
              editable={editable}
              strategy={strategy}
              cashflowEditable={cashflowEditable}
              assetLiabilityEditable={assetLiabilityEditable}
              reinvestmentsEditable={reinvestmentsEditable}
              calculatedStrategy={calculatedStrategy}
              onDuplicateAssetLiabilityClick={onDuplicateAssetLiabilityClick}
              onAssetLiabilityEditClick={onAssetLiabilityEditClick}
              onAssetLiabilityDeleteClick={onAssetLiabilityDeleteClick}
              onAddEventClick={onAddEventClick}
              onEditCashflowClick={onEditCashflowClick}
              onDeleteCashflowClick={onDeleteCashflowClick}
              onAddCashflowClick={onAddCashflowClick}
              onAddLiabilityClick={onAddLiabilityClick}
              onEditEventClick={onEditEventClick}
              onNameUpdateSubmit={onItemQuickRenameSubmit}
              onOneOffEventDeleteClick={onOneOffEventDeleteClick}
              onAddReinvestmentOption={onAddReinvestmentOption}
              onEditReinvestmentOption={onEditReinvestmentOption}
              onDeleteReinvestmentOption={onDeleteReinvestmentOption}
              onDisableReinvestmentClick={onDisableReinvestmentOption}
              onDisableAllReinvestmentsCheck={onDisableAllReinvestmentsOptions}
              taxSummary={taxSummary}
              disabled={disabled}
              quickRenaming={quickRenaming}
              showCashflowTable={false}
              setBalanceSheetCashflowCsvMeta={setBalanceSheetCashflowCsvMeta}
            />
          )}
          {activeTab == 2 && (
            <BalanceSheet
              editable={editable}
              strategy={strategy}
              cashflowEditable={cashflowEditable}
              assetLiabilityEditable={assetLiabilityEditable}
              reinvestmentsEditable={reinvestmentsEditable}
              calculatedStrategy={calculatedStrategy}
              onDuplicateAssetLiabilityClick={onDuplicateAssetLiabilityClick}
              onAssetLiabilityEditClick={onAssetLiabilityEditClick}
              onAssetLiabilityDeleteClick={onAssetLiabilityDeleteClick}
              onAddEventClick={onAddEventClick}
              onEditCashflowClick={onEditCashflowClick}
              onDeleteCashflowClick={onDeleteCashflowClick}
              onAddCashflowClick={onAddCashflowClick}
              onAddLiabilityClick={onAddLiabilityClick}
              onEditEventClick={onEditEventClick}
              onNameUpdateSubmit={onItemQuickRenameSubmit}
              onOneOffEventDeleteClick={onOneOffEventDeleteClick}
              onAddReinvestmentOption={onAddReinvestmentOption}
              onEditReinvestmentOption={onEditReinvestmentOption}
              onDeleteReinvestmentOption={onDeleteReinvestmentOption}
              onDisableReinvestmentClick={onDisableReinvestmentOption}
              onDisableAllReinvestmentsCheck={onDisableAllReinvestmentsOptions}
              taxSummary={taxSummary}
              disabled={disabled}
              quickRenaming={quickRenaming}
              showCashflowTable={true}
              setBalanceSheetCashflowCsvMeta={setBalanceSheetCashflowCsvMeta}
            />
          )}
          {activeTab == 3 && (
            <RepeatingChanges
              disabled={disabled}
              editable={editable && reinvestmentsEditable}
              hasCyclicInvestments={calculatedStrategy.hasCyclicInvestments}
              reInvestments={calculatedStrategy.investments}
              onAddReinvestmentOption={onAddReinvestmentOption}
              assets={calculatedStrategy.assets}
              fixedIncomeAssets={calculatedStrategy.fixedIncomeAssets}
              liabilities={calculatedStrategy.liabilities}
              onEditReinvestmentClick={onEditReinvestmentOption}
              onDeleteReinvestmentClick={onDeleteReinvestmentOption}
              onDisableReinvestmentClick={onDisableReinvestmentOption}
              onDisableAllReinvestmentsCheck={onDisableAllReinvestmentsOptions}
            />
          )}
          {activeTab == 4 && (
            <OneOffEventSheet
              strategy={strategy}
              editable={editable && assetLiabilityEditable}
              onEditEventClick={onEditEventClick}
              onOneOffEventDeleteClick={onOneOffEventDeleteClick}
            />
          )}
          {activeTab == 5 && (
            <RiskManagement
              adminCompTable={adminCompTable}
              customerCompTable={customerCompTable}
              strategy={strategy}
              armageddon={armageddon}
              stressTestApplied={stressTestApplied}
              calculatedStrategy={calculatedStrategy}
              onAssetResort={onAssetResort}
              disabled={disabled}
              editable={editable}
            />
          )}
          {activeTab == 6 && <StrategyChart strategy={calculatedStrategy} />}
          {activeTab == 7 && (
            <ActionSummaryView
              strategy={actionSummaryStrategy}
              editable={editable && assetLiabilityEditable}
              onCancelAction={action => {
                if (action.isOneOffChange) {
                  onOneOffEventDeleteClick(action);
                } else {
                  const itemType = action.type === 'Asset' ? 'assets' : 'liabilities';
                  const item =
                    action.type === 'Asset'
                      ? calculatedStrategy.assets.find(a => a._id === action._id)
                      : calculatedStrategy.liabilities.find(l => l._id === action._id);
                  if (item) onAssetLiabilityDeleteClick(itemType, item);
                }
              }}
            />
          )}
          {activeTab == 8 && <StressTest strategy={calculatedStrategy} />}
        </div>
      )}
    </div>
  );
};

export default Strategy;
